import React, {useEffect, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
import {getMemberInfo, updateMarketingConsent} from '../../../api/memberApi';
import AreaSelectComponent from "../../../components/common/AreaSelectComponent";
import {updateArea} from "../../../api/commonApi";
import {defaultAlert} from "../../../utils/alert";

interface UserInfo {
    memberName: string;
    memberId: string;
    phone: string;
    gender: string;
    preferredArea: string;
    marketingConsent: string;
}

export default function MyInfo() {
    const navigate = useNavigate();
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [originalMainArea, setOriginalMainArea] = useState<string>("");
    const [originalSubArea, setOriginalSubArea] = useState<string>("");
    const [selectedMainArea, setSelectedMainArea] = useState<string>("");
    const [selectedSubArea, setSelectedSubArea] = useState<string>("");
    const [isAreaChanged, setIsAreaChanged] = useState<boolean>(false);
    const [isMarketingConsent, setMarketingConsent] = useState<boolean>(false);

    useEffect(() => {
        fetchUserInfo();
    }, []);

    const fetchUserInfo = async () => {
        try {
            setLoading(true);
            const response = await getMemberInfo();
            let data = response.data.data;
            setUserInfo(data);
            setOriginalMainArea(data.preferredAreaMajor || "");
            setOriginalSubArea(data.preferredAreaMinor || "");
            setSelectedMainArea(data.preferredAreaMajor || "");
            setSelectedSubArea(data.preferredAreaMinor || "");
            setMarketingConsent(data.marketingConsent || false);
        } catch (err) {
            setError(err instanceof Error ? err.message : '알 수 없는 오류가 발생했습니다.');
            console.error('Error fetching user info:', err);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        if (selectedSubArea && selectedSubArea !== originalSubArea) {
            setIsAreaChanged(true);
        } else {
            setIsAreaChanged(false);
        }
    }, [selectedSubArea, originalSubArea]);
    const handleSubAreaChange = (subAreaCode: string) => {
        // 시/군/구를 선택했을 때만 체크
        if (subAreaCode) {
            const isMainChanged = selectedMainArea !== originalMainArea;
            const isSubChanged = subAreaCode !== originalSubArea;

            // 둘 중 하나라도 변경되었으면 확인
            if (isMainChanged || isSubChanged) {
                if (window.confirm('선호 지역을 변경하시겠습니까?')) {
                    setSelectedSubArea(subAreaCode);
                    handleSaveArea(selectedMainArea, subAreaCode);
                } else {
                    // 취소하면 원래 값으로 복구
                    setSelectedMainArea(originalMainArea);
                    setSelectedSubArea(originalSubArea);
                }
            } else {
                setSelectedSubArea(subAreaCode);
            }
        } else {
            setSelectedSubArea(subAreaCode);
        }
    };

    // 주소 저장 API 호출
    const handleSaveArea = async (mainArea: string, subArea: string) => {
        try {
            const res = await updateArea(mainArea, subArea);


            defaultAlert('선호 지역이 변경되었습니다.');

            // 저장 성공 시 원본 데이터 업데이트
            setOriginalMainArea(mainArea);
            setOriginalSubArea(subArea);
            setIsAreaChanged(false);
        } catch (error) {
            console.error('Failed to update area:', error);
            defaultAlert('선호 지역 변경에 실패했습니다.');

            // 실패 시 원래 값으로 복구
            setSelectedMainArea(originalMainArea);
            setSelectedSubArea(originalSubArea);
        }
    };

    const handleMarketingConsentChange = async (value: boolean) => {

        if (isMarketingConsent === value) return;
        setMarketingConsent(value);
        try {
            const res = await updateMarketingConsent(value);
            if (res.data.code !== "00") {
                defaultAlert("마케팅 수신동의 변경에 실패했습니다.");
            }
        } catch (err) {
            console.error('Error updating marketing consent:', err);
        }
    };

    if (loading) {
        return (
            <main className="container type_gray no-bottom-nav">
                <div className="inner-box">
                    <p>로딩 중...</p>
                </div>
            </main>
        );
    }

    if (error) {
        return (
            <main className="container type_gray no-bottom-nav">
                <div className="inner-box">
                    <p className="error">{error}</p>
                    <button onClick={fetchUserInfo}>다시 시도</button>
                </div>
            </main>
        );
    }

    if (!userInfo) {
        return null;
    }

    return (
        <main className="container type_gray no-bottom-nav">
            <form className="form-box">
                <section className="bg-white">
                    <fieldset>
                        <legend>내 정보 관리 폼</legend>
                    </fieldset>
                    <div className="inner-box">
                        <div className="form-item">
                            <label htmlFor="memberName" className="input-tit">이름</label>
                            <div className="input-box">
                                <input type="text" id="name" value={userInfo.memberName} disabled/>
                            </div>
                        </div>
                        <div className="form-item">
                            <label htmlFor="memberId" className="input-tit">아이디</label>
                            <div className="input-box">
                                <input type="text" id="id" value={userInfo.memberId} disabled/>
                            </div>
                        </div>
                        <div className="form-item">
                            <label htmlFor="phone" className="input-tit">휴대폰번호</label>
                            <div className="input-box">
                                <input type="text" id="phone" value={userInfo.phone} disabled/>
                            </div>
                        </div>
                        <div className="form-item">
                            <label htmlFor="gender" className="input-tit">성별</label>
                            <div className="input-box">
                                {/* eslint-disable-next-line eqeqeq */}
                                <input type="text" id="gender" value={userInfo.gender=='M'?"남성":"여성"} disabled/>
                            </div>
                        </div>
                        <div className="form-item">
                            <label htmlFor="" className="input-tit">선호지역</label>
                            <AreaSelectComponent
                                selectedMainArea={selectedMainArea}
                                selectedSubArea={selectedSubArea}
                                onMainAreaChange={setSelectedMainArea}
                                onSubAreaChange={handleSubAreaChange}
                                required={false}
                            />
                        </div>
                        <div className="form-item">
                            <label className="input-tit">마케팅 수신동의</label>
                            <div className="radio-row">
                                <span className="text-radio">
                                    <input
                                        type="radio"
                                        id="radio1"
                                        name="favor"
                                        checked={isMarketingConsent}
                                        onChange={(e) => handleMarketingConsentChange(true)}
                                    />
                                    <label htmlFor="radio1">동의</label>
                                </span>
                                <span className="text-radio">
                                    <input
                                        type="radio"
                                        id="radio2"
                                        name="favor"
                                        checked={!isMarketingConsent}
                                        onChange={(e) => handleMarketingConsentChange(false)}
                                    />
                                    <label htmlFor="radio2">동의 안함</label>
                                </span>
                            </div>
                        </div>
                    </div>
                </section>
                <section className="bg-white">
                    <div className="inner">
                        <ul className="text-menu">
                            <li>
                                <Link to="/menu/changePassword">
                                    비밀번호 변경<i className="fa-solid fa-chevron-right"></i>
                                </Link>
                            </li>
                            <li>
                                <Link to="/mypage/phone-change">
                                    휴대폰번호 변경<i className="fa-solid fa-chevron-right"></i>
                                </Link>
                            </li>
                            <li>
                                <Link to="/menu/changePaymentPassword">
                                    결제비밀번호 변경<i className="fa-solid fa-chevron-right"></i>
                                </Link>
                            </li>
                        </ul>
                    </div>
                </section>
            </form>
        </main>
    );
}